/**
 * QCReviewDashboard — Entry point for the review queue.
 *
 * Shows the 31 priority languages ranked by what needs the most attention.
 * Each row has a "Start Review" button that drops into the single-card queue.
 *
 * Route: /polyglot/qc/review
 * API: GET /@polyglot/qc-review/languages
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Loader,
  Icon,
  Button,
  Label,
  Table,
  Breadcrumb,
  Progress,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';
import { TIER1, TIER2 } from '../constants/languageTiers';

export default function QCReviewDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/qc-review/languages`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em', maxWidth: '900px' }}>
        <Loader active inline="centered" content="Loading review data..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em', maxWidth: '900px' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const { languages, coverage, domains } = data;

  // Split into tiers by the frontend's classification (constants/
  // languageTiers), NOT the API's tier field/lists — the deployed backend
  // can lag it (zh-CN mislabel, 2026-07-04; backend fix rides Phase 3).
  const t1 = languages.filter((l) => TIER1.includes(l.language));
  const t2 = languages.filter((l) => TIER2.includes(l.language));

  // Languages with no findings (clean)
  const t1Clean = TIER1.filter((code) => !languages.find((l) => l.language === code));
  const t2Clean = TIER2.filter((code) => !languages.find((l) => l.language === code));

  const totalFindings = languages.reduce((s, l) => s + l.total_findings, 0);
  const totalActionable = languages.reduce((s, l) => s + l.actionable, 0);

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em', maxWidth: '900px' }}>
      <Helmet title="QC Review Queue" />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/polyglot">Polyglot</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Review Queue</Breadcrumb.Section>
      </Breadcrumb>

      <div className="polyglot-page-header">
        <h1>Review Queue</h1>
        <p className="subtitle">
          {totalFindings} findings across {languages.length} languages
          {totalActionable > 0 && ` (${totalActionable} categorized)`}
        </p>
      </div>

      <p style={{ color: '#555', fontSize: '1.05em', marginBottom: '1.5em' }}>
        Pick a language to start reviewing findings one at a time.
        Findings are ordered by type — mistranslations first, then incomplete,
        typos, and others. Strings with suggested fixes come first within each type.
      </p>

      {/* Tier 1 — EU Official */}
      <Header as="h2" style={{ marginTop: '1.5em' }}>
        <Label color="blue" horizontal>Tier 1</Label>
        EU Official Languages
      </Header>
      <LanguageTable languages={t1} coverage={coverage} domains={domains} />
      {t1Clean.length > 0 && (
        <div style={{ color: '#888', fontSize: '0.9em', marginTop: '0.5em' }}>
          Clean (no pending findings): {t1Clean.join(', ')}
        </div>
      )}

      {/* Tier 2 — Partner */}
      <Header as="h2" style={{ marginTop: '2em' }}>
        <Label color="teal" horizontal>Tier 2</Label>
        Partner Languages
      </Header>
      <LanguageTable languages={t2} coverage={coverage} domains={domains} />
      {t2Clean.length > 0 && (
        <div style={{ color: '#888', fontSize: '0.9em', marginTop: '0.5em' }}>
          Clean (no pending findings): {t2Clean.join(', ')}
        </div>
      )}

      <Segment basic style={{ marginTop: '2em', textAlign: 'center' }}>
        <Link to="/polyglot/qc">
          <Button basic>
            <Icon name="chart bar" /> Full QC Dashboard
          </Button>
        </Link>
        <Link to="/polyglot/qc/findings">
          <Button basic>
            <Icon name="list" /> Browse All Findings
          </Button>
        </Link>
      </Segment>
    </Container>
  );
}

const LanguageTable = ({ languages, coverage, domains }) => {
  if (!languages || languages.length === 0) {
    return (
      <Segment>
        <Icon name="check circle" color="green" /> All clean — no pending findings.
      </Segment>
    );
  }

  return (
    <Table compact celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Language</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Coverage</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Findings</Table.HeaderCell>
          <Table.HeaderCell>Domains</Table.HeaderCell>
          <Table.HeaderCell textAlign="center" style={{ width: '120px' }} />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {languages.map((lang) => {
          const cov = coverage[lang.language];
          const covPct = cov?.pct || 0;
          const langDomains = (domains && domains[lang.language]) || [];

          return (
            <Table.Row key={lang.language}>
              <Table.Cell>
                <strong style={{ fontSize: '1.1em' }}>{lang.language}</strong>
                {lang.language_name_english && (
                  <span style={{ color: '#666', marginLeft: '0.5em' }}>
                    {lang.language_name_english}
                  </span>
                )}
              </Table.Cell>
              <Table.Cell textAlign="center">
                <span style={{ color: covPct >= 95 ? '#21ba45' : covPct >= 70 ? '#f2711c' : '#db2828' }}>
                  {covPct}%
                </span>
              </Table.Cell>
              <Table.Cell textAlign="center">
                <strong>{lang.total_findings}</strong>
              </Table.Cell>
              <Table.Cell>
                {langDomains.length <= 1 ? (
                  <span style={{ color: '#888', fontSize: '0.9em' }}>
                    {langDomains[0]?.domain || 'all'} ({langDomains[0]?.findings || lang.total_findings})
                  </span>
                ) : (
                  <div style={{ display: 'flex', gap: '0.3em', flexWrap: 'wrap' }}>
                    {langDomains.map((d) => (
                      <Label
                        key={`${d.domain}-${d.platform}`}
                        as={Link}
                        to={`/polyglot/qc/review/work?language=${lang.language}&platform=${d.platform}`}
                        size="mini"
                        style={{ cursor: 'pointer' }}
                      >
                        {d.domain === d.platform ? d.domain : `${d.domain} (${d.platform})`}
                        <Label.Detail>{d.findings}</Label.Detail>
                      </Label>
                    ))}
                  </div>
                )}
              </Table.Cell>
              <Table.Cell textAlign="center">
                <Button
                  as={Link}
                  to={`/polyglot/qc/review/work?language=${lang.language}`}
                  color="blue"
                  size="small"
                  compact
                >
                  Review All
                </Button>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};
